"use client";

import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../components/motionVariants";
import DocsPageFrame from "./DocsPageFrame";
import Callout from "./Callout";

// ─────────────────────────────────────────────────────────────────────────
// Data-driven renderer shared by every "Bulk Import" documentation page.
// A page supplies a single data object; the layout, table styling, table of
// contents, and callouts are all handled here so the pages stay consistent.
// ─────────────────────────────────────────────────────────────────────────

const SECTIONS = [
  { heading: "How it works" },
  { heading: "Required in every row" },
  { heading: "Optional fields with rules" },
  { heading: "Must match existing records" },
  { heading: "Duplicates & uniqueness" },
  { heading: "Common mistakes" },
  { heading: "What happens on the final step" },
  { heading: "Recommended way to work" },
];

const TwoColTable = ({ head, rows }) => (
  <div className="overflow-x-auto rounded-xl border border-[#E5E5E5] my-4">
    <table className="w-full border-collapse text-sm">
      <thead>
        <tr className="bg-[#111827] text-white">
          <th className="text-left geist-semibold px-4 py-2.5 w-[34%]">
            {head[0]}
          </th>
          <th className="text-left geist-semibold px-4 py-2.5">{head[1]}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr
            key={i}
            className={`border-t border-[#E5E5E5] ${i % 2 ? "bg-[#FAFAFA]" : "bg-white"}`}
          >
            <td className="px-4 py-2.5 geist-medium text-[#111827] align-top">
              {r[0]}
            </td>
            <td className="px-4 py-2.5 geist-regular text-[#374151] leading-relaxed align-top">
              {r[1]}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const SectionHeading = ({ id, children }) => (
  <h2
    id={id}
    className="text-[24px] sentient-medium text-[#111827] mb-3 mt-2 scroll-mt-24"
  >
    {children}
  </h2>
);

const Para = ({ children }) => (
  <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
    {children}
  </p>
);

export default function BulkImportArticle({ data }) {
  const {
    title,
    audience,
    intro,
    createsOnlyNote,
    steps,
    requiredFields,
    conditionalFields,
    matchFields,
    duplicateRules,
    mistakes,
    onApply,
    workflow,
    calloutsAfterSteps = [],
    matchNote,
  } = data;

  return (
    <DocsPageFrame sections={SECTIONS}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        {/* Header */}
        <motion.div variants={fadeUp} className="mb-8">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            {title}
          </h1>
          {audience ? (
            <h2 className="text-[20px] sentient-medium text-[#111827] mb-3">
              {audience}
            </h2>
          ) : null}
          {intro.map((p, i) => (
            <p
              key={i}
              className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl mb-3"
            >
              {p}
            </p>
          ))}
          {createsOnlyNote ? (
            <Callout type="warning">{createsOnlyNote}</Callout>
          ) : null}
        </motion.div>

        <hr className="my-8" />

        {/* How it works */}
        <motion.div variants={fadeUp} className="mb-12">
          <SectionHeading id="section-0">How it works</SectionHeading>
          <Para>
            Every bulk upload has two steps. You run the same sheet through a{" "}
            <span className="geist-medium text-[#111827]">Check</span> first, fix
            anything it flags, then run it again to actually{" "}
            <span className="geist-medium text-[#111827]">
              {steps.commitVerb}
            </span>
            .
          </Para>
          <TwoColTable
            head={["Step", "What it does"]}
            rows={[
              [
                "1. Check (recommended)",
                "Reviews your entire sheet and lists every row with a problem — without saving anything. Fix the issues and check again.",
              ],
              [
                `2. ${steps.commitLabel}`,
                `Once every row passes, upload again to actually ${steps.commitVerb.toLowerCase()}.`,
              ],
            ]}
          />
          <Callout type="danger">
            {steps.allOrNothing}
          </Callout>
          {calloutsAfterSteps.map((c, i) => (
            <Callout key={i} type={c.type}>
              {c.text}
            </Callout>
          ))}
        </motion.div>

        {/* Required */}
        <motion.div variants={fadeUp} className="mb-12">
          <SectionHeading id="section-1">Required in every row</SectionHeading>
          <Para>These must be filled in correctly on every row:</Para>
          <TwoColTable
            head={["Field", "What's needed"]}
            rows={requiredFields.map((f) => [f.field, f.needs])}
          />
        </motion.div>

        {/* Optional with rules */}
        {conditionalFields?.length ? (
          <motion.div variants={fadeUp} className="mb-12">
            <SectionHeading id="section-2">
              Optional fields with rules
            </SectionHeading>
            <Para>
              These are optional — but if you fill them in, they must follow
              these rules:
            </Para>
            <TwoColTable
              head={["Field", "Rule if you fill it in"]}
              rows={conditionalFields.map((f) => [f.field, f.rule])}
            />
          </motion.div>
        ) : (
          <div id="section-2" />
        )}

        {/* Must match existing */}
        {matchFields?.length ? (
          <motion.div variants={fadeUp} className="mb-12">
            <SectionHeading id="section-3">
              Must match existing records
            </SectionHeading>
            <Para>
              {matchNote ||
                "If you fill these in, the value must exactly match a record already set up for your company. The match is case-sensitive — same spelling and the same uppercase/lowercase letters."}
            </Para>
            <TwoColTable
              head={["Field", "What it must match"]}
              rows={matchFields.map((f) => [f.field, f.rule])}
            />
          </motion.div>
        ) : (
          <div id="section-3" />
        )}

        {/* Duplicates */}
        {duplicateRules?.length ? (
          <motion.div variants={fadeUp} className="mb-12">
            <SectionHeading id="section-4">
              Duplicates &amp; uniqueness
            </SectionHeading>
            <TwoColTable
              head={["Situation", "What happens"]}
              rows={duplicateRules.map((r) => [r.condition, r.result])}
            />
          </motion.div>
        ) : (
          <div id="section-4" />
        )}

        {/* Common mistakes */}
        <motion.div variants={fadeUp} className="mb-12">
          <SectionHeading id="section-5">Common mistakes</SectionHeading>
          <Para>If a row is rejected, it is usually for one of these reasons:</Para>
          <TwoColTable
            head={["If this happens…", "…here's what it means"]}
            rows={mistakes.map((m) => [m.problem, m.meaning])}
          />
        </motion.div>

        {/* What happens on apply */}
        <motion.div variants={fadeUp} className="mb-12">
          <SectionHeading id="section-6">
            What happens on the final step
          </SectionHeading>
          <ul className="space-y-2.5">
            {onApply.map((item, i) => (
              <li
                key={i}
                className="flex items-start gap-2 text-[15px] geist-regular text-[#374151] leading-relaxed"
              >
                <span className="text-[#111827] flex-shrink-0 mt-1">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </motion.div>

        {/* Recommended workflow */}
        <motion.div variants={fadeUp} className="mb-4">
          <SectionHeading id="section-7">Recommended way to work</SectionHeading>
          <ol className="space-y-2.5 list-decimal pl-5">
            {workflow.map((item, i) => (
              <li
                key={i}
                className="text-[15px] geist-regular text-[#374151] leading-relaxed pl-1"
              >
                {item}
              </li>
            ))}
          </ol>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
